import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, BellOff, Check, Settings, Smartphone, Monitor, X } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../lib/api';

const isNotificationSupported = typeof window !== 'undefined' && 'Notification' in window;

const PushNotificationsManager = ({ userId }) => {
    const [permission, setPermission] = useState(isNotificationSupported ? Notification.permission : 'denied');
    const [subscribed, setSubscribed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [vapidKey, setVapidKey] = useState(null);
    const [devices, setDevices] = useState([]);
    const [showPrefs, setShowPrefs] = useState(false);
    const [prefs, setPrefs] = useState({
        new_mandate: true,
        signature_pending: true,
        signature_complete: true,
        visit_reminder: true,
        offer_received: true,
        feedback_request: true,
        weekly_report: true
    });

    useEffect(() => {
        init();
    }, [userId]);

    const init = async () => {
        try {
            // Get VAPID key
            const keyResponse = await api.get('/push/vapid-key');
            setVapidKey(keyResponse.data.publicKey);

            // Check if already subscribed
            const subsResponse = await api.get(`/push/subscriptions?user_id=${userId}`);
            setDevices(subsResponse.data.subscriptions || []);
            setSubscribed(subsResponse.data.count > 0);

            // Get preferences
            const prefsResponse = await api.get(`/push/preferences?user_id=${userId}`);
            setPrefs(prefsResponse.data);
        } catch (err) {
            console.error('Error initializing push:', err);
        } finally {
            setLoading(false);
        }
    };

    const requestPermission = async () => {
        if (!isNotificationSupported) return 'denied';
        const result = await Notification.requestPermission();
        setPermission(result);
        return result;
    };

    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const subscribe = async () => {
        try {
            // Request permission first
            if (permission !== 'granted') {
                const result = await requestPermission();
                if (result !== 'granted') {
                    alert('Vous devez autoriser les notifications pour continuer');
                    return;
                }
            }

            if (!vapidKey) {
                alert('Notifications push non configurées');
                return;
            }

            // Register service worker
            const registration = await navigator.serviceWorker.register('/sw.js');
            await navigator.serviceWorker.ready;

            // Subscribe to push
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey)
            });

            // Send subscription to server
            const subJson = subscription.toJSON();
            await api.post('/push/subscribe', {
                endpoint: subJson.endpoint,
                keys: subJson.keys,
                user_id: userId,
                device_name: getDeviceName(),
                device_type: 'web'
            });

            setSubscribed(true);
            
            // Refresh devices list
            const subsResponse = await api.get(`/push/subscriptions?user_id=${userId}`);
            setDevices(subsResponse.data.subscriptions || []);

        } catch (err) {
            console.error('Error subscribing:', err);
            alert('Erreur lors de l\'activation des notifications');
        }
    };

    const unsubscribe = async () => {
        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
                const subscription = await registration.pushManager.getSubscription();
                if (subscription) {
                    await subscription.unsubscribe();
                    await api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`);
                }
            }
            setSubscribed(false);
            setDevices([]);
        } catch (err) {
            console.error('Error unsubscribing:', err);
        }
    };

    const sendTestNotification = async () => {
        try {
            await api.post(`/push/send-test?user_id=${userId}`);
            alert('Notification de test envoyée !');
        } catch (err) {
            console.error('Error sending test:', err);
            alert('Erreur lors de l\'envoi');
        }
    };

    const updatePrefs = async (key, value) => {
        const newPrefs = { ...prefs, [key]: value };
        setPrefs(newPrefs);
        
        try {
            await api.put(`/push/preferences?user_id=${userId}`, newPrefs);
        } catch (err) {
            console.error('Error updating preferences:', err);
        }
    };

    const getDeviceName = () => {
        const ua = navigator.userAgent;
        if (ua.includes('Chrome')) return 'Chrome';
        if (ua.includes('Firefox')) return 'Firefox';
        if (ua.includes('Safari')) return 'Safari';
        if (ua.includes('Edge')) return 'Edge';
        return 'Navigateur';
    };

    if (loading) {
        return (
            <div className="bg-card rounded-xl p-4 border border-border animate-pulse">
                <div className="h-6 bg-muted rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
            </div>
        );
    }

    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
        return (
            <div className="bg-card rounded-xl p-4 border border-border">
                <div className="flex items-center gap-3 text-muted-foreground">
                    <BellOff size={20} />
                    <span>Notifications push non supportées par ce navigateur</span>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card backdrop-blur-xl rounded-xl p-5 border border-border"
        >
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${subscribed ? 'bg-green-500/20' : 'bg-muted'}`}>
                        <Bell className={subscribed ? 'text-green-400' : 'text-muted-foreground'} size={20} />
                    </div>
                    <div>
                        <h3 className="font-semibold text-foreground">Notifications Push</h3>
                        <p className="text-sm text-muted-foreground">
                            {subscribed 
                                ? `${devices.length} appareil(s) connecté(s)` 
                                : 'Recevez des alertes en temps réel'}
                        </p>
                    </div>
                </div>
                
                {subscribed && (
                    <button 
                        onClick={() => setShowPrefs(!showPrefs)}
                        className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <Settings size={18} />
                    </button>
                )}
            </div>

            {/* Preferences panel */}
            {showPrefs && subscribed && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-4 p-4 bg-card rounded-lg border border-border"
                >
                    <h4 className="text-sm font-medium text-foreground mb-3">Types de notifications</h4>
                    <div className="space-y-2">
                        {[
                            { key: 'new_mandate', label: 'Nouveau mandat signé' },
                            { key: 'signature_pending', label: 'Signature en attente' },
                            { key: 'offer_received', label: 'Nouvelle offre reçue' },
                            { key: 'visit_reminder', label: 'Rappel de visite' },
                            { key: 'feedback_request', label: 'Demande de feedback' },
                            { key: 'weekly_report', label: 'Rapport hebdomadaire' }
                        ].map(({ key, label }) => (
                            <label key={key} className="flex items-center justify-between cursor-pointer group">
                                <span className="text-sm text-muted-foreground group-hover:text-foreground">{label}</span>
                                <div 
                                    onClick={() => updatePrefs(key, !prefs[key])}
                                    className={`w-10 h-6 rounded-full transition-colors relative ${
                                        prefs[key] ? 'bg-green-500' : 'bg-white/20'
                                    }`}
                                >
                                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                                        prefs[key] ? 'translate-x-5' : 'translate-x-1'
                                    }`} />
                                </div>
                            </label>
                        ))}
                    </div>
                </motion.div>
            )}

            {/* Actions */}
            {subscribed ? (
                <div className="flex flex-wrap gap-2">
                    <Button
                        onClick={sendTestNotification}
                        size="sm"
                        className="bg-purple-500 hover:bg-purple-600 text-white"
                    >
                        <Bell className="w-4 h-4 mr-2" />
                        Tester
                    </Button>
                    <Button
                        onClick={unsubscribe}
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                        <BellOff className="w-4 h-4 mr-2" />
                        Désactiver
                    </Button>
                </div>
            ) : (
                <Button
                    onClick={subscribe}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                >
                    <Bell className="w-4 h-4 mr-2" />
                    Activer les notifications
                </Button>
            )}

            {permission === 'denied' && (
                <p className="text-xs text-red-400 mt-3">
                    Les notifications sont bloquées. Modifiez les paramètres de votre navigateur.
                </p>
            )}
        </motion.div>
    );
};

export default PushNotificationsManager;
